/**
 * Data-driven builders for all ERP module agent workspace tabs.
 */

import { AGENT_API, fetchParallel, safeGet, safeGetDocuments } from '../agentApiPaths';
import {
  buildInboxItem,
  buildListPanelItem,
  buildPromptFromRecord,
  filterActionable,
  formatShortDate,
  INBOX_PRIORITY,
  pickField,
  translateRecordStatusLabel,
} from '../agentPanelTypes';
import { getWorkspaceTabsForModule } from '../../config/agentWorkspaceTabs';
import { mp } from '../../config/agentModuleI18n';
import i18n from '../../../i18n';

const gb = (key, opts) => i18n.t(`modules.shared.aiAgent.genericBuilder.${key}`, opts);
const gbi = (moduleId, inboxKey, field, opts = {}) =>
  i18n.t(`modules.shared.aiAgent.genericBuilder.modules.${moduleId}.inbox.${inboxKey}.${field}`, opts);

function resolveInboxItem(moduleId, item) {
  return {
    ...item,
    id: moduleId,
    summaryTitle: gbi(moduleId, item.key, 'summaryTitle'),
    summaryPrompt: gbi(moduleId, item.key, 'summaryPrompt'),
  };
}

function moduleDef(moduleId, def) {
  return {
    id: moduleId,
    label: mp(moduleId, 'shortLabel'),
    fetch: def.fetch,
    inbox: (def.inbox || []).map((item) => resolveInboxItem(moduleId, item)),
    panels: def.panels,
  };
}

const PANEL_LIMIT = 12;
const INBOX_LIMIT = 6;

const DEFAULT_TITLE_FIELDS = ['title', 'name', 'label', 'reference', 'number', 'code', 'id'];
const DEFAULT_SUBTITLE_FIELDS = ['description', 'subtitle', 'client_name', 'clientName', 'employee_name', 'status', 'type'];
const DEFAULT_STATUS_FIELDS = ['status', 'state', 'statut'];

function resolveStatusTone(status) {
  const value = String(status || '').toLowerCase();
  if (!value) return 'slate';
  if (['done', 'completed', 'payé', 'paye', 'paid', 'closed', 'fermé', 'ferme', 'validated'].some((s) => value.includes(s))) {
    return 'green';
  }
  if (['urgent', 'overdue', 'retard', 'critical', 'critique', 'rejected', 'refusé'].some((s) => value.includes(s))) {
    return 'red';
  }
  if (['pending', 'open', 'draft', 'brouillon', 'en cours', 'active', 'planned'].some((s) => value.includes(s))) {
    return 'amber';
  }
  if (['partial', 'in_progress', 'processing'].some((s) => value.includes(s))) {
    return 'blue';
  }
  return 'slate';
}

function mapRecordToPanelItem(record, config, moduleLabel) {
  const title = pickField(record, config.titleFields || DEFAULT_TITLE_FIELDS) || `${moduleLabel} #${record.id || '—'}`;
  const subtitleParts = [
    pickField(record, config.subtitleFields || DEFAULT_SUBTITLE_FIELDS),
    config.includeDate !== false ? formatShortDate(pickField(record, ['due_date', 'dueDate', 'date', 'created_at', 'createdAt'])) : '',
  ].filter(Boolean);
  const status = pickField(record, DEFAULT_STATUS_FIELDS);
  const promptTemplate =
    config.promptTemplate ||
    gb('default_panel_prompt', { module: moduleLabel });

  return buildListPanelItem({
    id: record.id || title,
    title,
    subtitle: subtitleParts.join(' • ') || moduleLabel,
    statusLabel: status ? translateRecordStatusLabel(status) : '—',
    statusTone: resolveStatusTone(status),
    prompt: buildPromptFromRecord(record, promptTemplate, {
      title: config.titleFields || DEFAULT_TITLE_FIELDS,
      subtitle: config.subtitleFields || DEFAULT_SUBTITLE_FIELDS,
      status: DEFAULT_STATUS_FIELDS,
      date: ['due_date', 'dueDate', 'date'],
      reference: ['reference', 'number', 'code'],
    }),
  });
}

function mapRecordsToPanel(records = [], config, moduleLabel) {
  return records.slice(0, PANEL_LIMIT).map((record) => mapRecordToPanelItem(record, config, moduleLabel));
}

function buildInboxItemsFromSource(records = [], config, moduleLabel) {
  const actionable = config.filterActionable === false ? records : filterActionable(records, DEFAULT_STATUS_FIELDS);
  const slice = actionable.slice(0, INBOX_LIMIT);

  if (slice.length === 0) return [];

  if (slice.length >= 3 && config.summaryPrompt) {
    return [
      buildInboxItem({
        id: `${config.key}-summary`,
        title: gbi(config.id, config.key, 'summaryTitle', { count: slice.length }),
        subtitle: config.summarySubtitle || moduleLabel,
        priority: config.priority || INBOX_PRIORITY.MEDIUM,
        icon: config.icon || 'alert',
        iconTone: config.iconTone || 'orange',
        primaryAction: true,
        prompt: gbi(config.id, config.key, 'summaryPrompt', { count: slice.length }),
      }),
    ];
  }

  return slice.map((record, index) => {
    const title = pickField(record, config.titleFields || DEFAULT_TITLE_FIELDS) || `${moduleLabel} ${index + 1}`;
    const promptTemplate =
      config.inboxPromptTemplate ||
      gb('default_inbox_prompt', { module: moduleLabel });

    return buildInboxItem({
      id: `${config.key}-${record.id || index}`,
      title,
      subtitle: pickField(record, config.subtitleFields || DEFAULT_SUBTITLE_FIELDS) || moduleLabel,
      priority: config.priority || INBOX_PRIORITY.MEDIUM,
      icon: config.icon || 'alert',
      iconTone: config.iconTone || 'orange',
      primaryAction: index === 0,
      prompt: buildPromptFromRecord(record, promptTemplate, {
        title: config.titleFields || DEFAULT_TITLE_FIELDS,
        status: DEFAULT_STATUS_FIELDS,
        reference: ['reference', 'number', 'code'],
      }),
    });
  });
}

function buildDocumentsPanel(documents = [], moduleLabel) {
  return mapRecordsToPanel(
    documents,
    {
      titleFields: ['number', 'reference', 'title', 'name'],
      subtitleFields: ['client_name', 'clientName', 'type', 'category'],
      promptTemplate: gb('document_panel_prompt', { module: moduleLabel }),
      includeDate: true,
    },
    moduleLabel,
  );
}

/** @typedef {Object} ModuleBuilderConfig */
/** @type {Record<string, ModuleBuilderConfig>} */
export const MODULE_BUILDER_CONFIGS = {
  inventaire: moduleDef('inventaire', {
    fetch: () =>
      fetchParallel({
        alerts: AGENT_API.inventaire.stockAlerts,
        movements: AGENT_API.inventaire.movements,
        warehouses: AGENT_API.inventaire.warehouses,
        replenishments: AGENT_API.inventaire.replenishments,
      }),
    inbox: [
      { key: 'alerts', dataKey: 'alerts', titleFields: ['product_name', 'name'] },
      { key: 'replenishments', dataKey: 'replenishments', titleFields: ['reference', 'product_name'] },
    ],
    panels: {
      movements: { dataKey: 'movements', titleFields: ['reference', 'type'], subtitleFields: ['warehouse_name', 'quantity'] },
      stock: { dataKey: 'warehouses', titleFields: ['name', 'warehouse_name'], subtitleFields: ['location', 'capacity'] },
    },
  }),
  achats: moduleDef('achats', {
    fetch: () =>
      fetchParallel({
        demandes: AGENT_API.achats.demandes,
        commandes: AGENT_API.achats.commandes,
        receptions: AGENT_API.achats.receptions,
      }),
    inbox: [
      { key: 'demandes', dataKey: 'demandes' },
      { key: 'commandes', dataKey: 'commandes' },
    ],
    panels: {
      commandes: { dataKey: 'commandes', titleFields: ['number', 'reference'], subtitleFields: ['supplier_name', 'total'] },
      receptions: { dataKey: 'receptions', titleFields: ['number', 'reference'], subtitleFields: ['supplier_name', 'status'] },
    },
  }),
  ventes: moduleDef('ventes', {
    fetch: () =>
      fetchParallel({
        commandes: AGENT_API.ventes.commandes,
        livraisons: AGENT_API.ventes.livraisons,
        pipeline: AGENT_API.ventes.pipeline,
      }),
    inbox: [
      { key: 'commandes', dataKey: 'commandes' },
      { key: 'pipeline', dataKey: 'pipeline' },
    ],
    panels: {
      pipeline: { dataKey: 'pipeline', titleFields: ['title', 'name', 'client_name'], subtitleFields: ['stage', 'amount'] },
      livraisons: { dataKey: 'livraisons', titleFields: ['number', 'reference'], subtitleFields: ['client_name', 'status'] },
    },
  }),
  rh: moduleDef('rh', {
    fetch: () =>
      fetchParallel({
        employees: AGENT_API.rh.employees,
        conges: AGENT_API.rh.conges,
        departures: AGENT_API.rh.departures,
      }),
    inbox: [
      { key: 'conges', dataKey: 'conges' },
      { key: 'departures', dataKey: 'departures' },
    ],
    panels: {
      employes: { dataKey: 'employees', titleFields: ['full_name', 'name'], subtitleFields: ['department', 'position'] },
      conges: { dataKey: 'conges', titleFields: ['employee_name', 'name'], subtitleFields: ['type', 'start_date'] },
    },
  }),
  pointage: moduleDef('pointage', {
    fetch: () =>
      fetchParallel({
        absences: AGENT_API.pointage.absences,
        lateness: AGENT_API.pointage.lateness,
        conges: AGENT_API.pointage.conges,
        daily: AGENT_API.pointage.daily,
      }),
    inbox: [
      { key: 'absences', dataKey: 'absences' },
      { key: 'lateness', dataKey: 'lateness' },
    ],
    panels: {
      pointage: { dataKey: 'daily', titleFields: ['employee_name', 'name'], subtitleFields: ['date', 'hours'] },
      conges: { dataKey: 'conges', titleFields: ['employee_name', 'name'], subtitleFields: ['type', 'status'] },
    },
  }),
  salaires: moduleDef('salaires', {
    fetch: () =>
      fetchParallel({
        journal: AGENT_API.salaires.journal,
        validation: AGENT_API.salaires.validation,
        virements: AGENT_API.salaires.virements,
        cnas: AGENT_API.salaires.cnas,
      }),
    inbox: [
      { key: 'validation', dataKey: 'validation' },
      { key: 'virements', dataKey: 'virements' },
    ],
    panels: {
      paies: { dataKey: 'journal', titleFields: ['period', 'reference'], subtitleFields: ['employee_count', 'total'] },
      cnas: { dataKey: 'cnas', titleFields: ['period', 'reference'], subtitleFields: ['status', 'amount'] },
    },
  }),
  situation: moduleDef('situation', {
    fetch: () =>
      fetchParallel({
        incidents: AGENT_API.situation.incidents,
        crises: AGENT_API.situation.crises,
        plans: AGENT_API.situation.plans,
      }),
    inbox: [
      { key: 'incidents', dataKey: 'incidents', priority: INBOX_PRIORITY.HIGH },
      { key: 'crises', dataKey: 'crises', priority: INBOX_PRIORITY.HIGH },
    ],
    panels: {
      incidents: { dataKey: 'incidents', titleFields: ['title', 'name'], subtitleFields: ['severity', 'status'] },
      crises: { dataKey: 'crises', titleFields: ['title', 'name'], subtitleFields: ['level', 'status'] },
    },
  }),
  caisse: moduleDef('caisse', {
    fetch: () =>
      fetchParallel({
        journal: AGENT_API.caisse.journal,
        vouchers: AGENT_API.caisse.vouchers,
        reconciliation: AGENT_API.caisse.reconciliation,
      }),
    inbox: [
      { key: 'reconciliation', dataKey: 'reconciliation' },
      { key: 'journal', dataKey: 'journal' },
    ],
    panels: {
      journal: { dataKey: 'journal', titleFields: ['reference', 'label'], subtitleFields: ['amount', 'type'] },
      bons: { dataKey: 'vouchers', titleFields: ['number', 'reference'], subtitleFields: ['beneficiary', 'amount'] },
    },
  }),
  banque: moduleDef('banque', {
    fetch: () =>
      fetchParallel({
        accounts: AGENT_API.banque.accounts,
        reconciliation: AGENT_API.banque.reconciliation,
        checks: AGENT_API.banque.checks,
        transfers: AGENT_API.banque.transfers,
      }),
    inbox: [
      { key: 'reconciliation', dataKey: 'reconciliation' },
      { key: 'checks', dataKey: 'checks' },
    ],
    panels: {
      comptes: { dataKey: 'accounts', titleFields: ['name', 'account_name'], subtitleFields: ['iban', 'balance'] },
      virements: { dataKey: 'transfers', titleFields: ['reference', 'number'], subtitleFields: ['beneficiary', 'amount'] },
    },
  }),
  comptabilite: moduleDef('comptabilite', {
    fetch: () =>
      fetchParallel({
        entries: AGENT_API.comptabilite.entries,
        balance: AGENT_API.comptabilite.balance,
        journal: AGENT_API.comptabilite.journal,
      }),
    inbox: [{ key: 'entries', dataKey: 'entries' }],
    panels: {
      ecritures: { dataKey: 'entries', titleFields: ['reference', 'number'], subtitleFields: ['account', 'amount'] },
      balance: { dataKey: 'balance', titleFields: ['account', 'account_name'], subtitleFields: ['debit', 'credit'], filterActionable: false },
    },
  }),
  tresorerie: moduleDef('tresorerie', {
    fetch: () =>
      fetchParallel({
        position: AGENT_API.tresorerie.position,
        investments: AGENT_API.tresorerie.investments,
        liquidity: AGENT_API.tresorerie.liquidity,
      }),
    inbox: [{ key: 'liquidity', dataKey: 'liquidity' }],
    panels: {
      position: { dataKey: 'position', titleFields: ['account', 'label'], subtitleFields: ['balance', 'currency'], filterActionable: false },
      placements: { dataKey: 'investments', titleFields: ['name', 'label'], subtitleFields: ['amount', 'maturity'] },
    },
  }),
  budget: moduleDef('budget', {
    fetch: () =>
      fetchParallel({
        variance: AGENT_API.budget.variance,
        workflow: AGENT_API.budget.workflow,
        engagements: AGENT_API.budget.engagements,
      }),
    inbox: [
      { key: 'variance', dataKey: 'variance' },
      { key: 'workflow', dataKey: 'workflow' },
    ],
    panels: {
      ecarts: { dataKey: 'variance', titleFields: ['line', 'category'], subtitleFields: ['variance', 'percent'] },
      engagements: { dataKey: 'engagements', titleFields: ['reference', 'label'], subtitleFields: ['amount', 'status'] },
    },
  }),
  projets: moduleDef('projets', {
    fetch: () =>
      fetchParallel({
        tasks: AGENT_API.projets.tasks,
        alerts: AGENT_API.projets.alerts,
        milestones: AGENT_API.projets.milestones,
        resources: AGENT_API.projets.resources,
      }),
    inbox: [
      { key: 'alerts', dataKey: 'alerts' },
      { key: 'milestones', dataKey: 'milestones' },
    ],
    panels: {
      taches: { dataKey: 'tasks', titleFields: ['title', 'name'], subtitleFields: ['project', 'assignee'] },
      ressources: { dataKey: 'resources', titleFields: ['name', 'resource_name'], subtitleFields: ['role', 'availability'] },
    },
  }),
  contrats: moduleDef('contrats', {
    fetch: () =>
      fetchParallel({
        expiryAlerts: AGENT_API.contrats.expiryAlerts,
        clients: AGENT_API.contrats.clients,
        renewals: AGENT_API.contrats.renewals,
        approvals: AGENT_API.contrats.approvals,
      }),
    inbox: [
      { key: 'expiryAlerts', dataKey: 'expiryAlerts' },
      { key: 'approvals', dataKey: 'approvals' },
    ],
    panels: {
      echeances: { dataKey: 'expiryAlerts', titleFields: ['contract_name', 'title'], subtitleFields: ['client_name', 'expiry_date'] },
      renouvellements: { dataKey: 'renewals', titleFields: ['contract_name', 'title'], subtitleFields: ['client_name', 'renewal_date'] },
    },
  }),
  production: moduleDef('production', {
    fetch: () =>
      fetchParallel({
        orders: AGENT_API.production.orders,
        breakdowns: AGENT_API.production.breakdowns,
        nonconformities: AGENT_API.production.nonconformities,
      }),
    inbox: [
      { key: 'orders', dataKey: 'orders' },
      { key: 'breakdowns', dataKey: 'breakdowns' },
    ],
    panels: {
      ordres: { dataKey: 'orders', titleFields: ['reference', 'number'], subtitleFields: ['product', 'status'] },
      qualite: { dataKey: 'nonconformities', titleFields: ['reference', 'title'], subtitleFields: ['severity', 'status'] },
    },
  }),
  maintenance: moduleDef('maintenance', {
    fetch: () =>
      fetchParallel({
        tickets: AGENT_API.maintenance.tickets,
        emergency: AGENT_API.maintenance.emergency,
        planning: AGENT_API.maintenance.planning,
      }),
    inbox: [
      { key: 'emergency', dataKey: 'emergency', priority: INBOX_PRIORITY.HIGH },
      { key: 'tickets', dataKey: 'tickets' },
    ],
    panels: {
      tickets: { dataKey: 'tickets', titleFields: ['title', 'reference'], subtitleFields: ['equipment', 'priority'] },
      planning: { dataKey: 'planning', titleFields: ['title', 'equipment'], subtitleFields: ['scheduled_date', 'type'] },
    },
  }),
  qualite: moduleDef('qualite', {
    fetch: () =>
      fetchParallel({
        nonConformites: AGENT_API.qualite.nonConformites,
        audits: AGENT_API.qualite.audits,
        actions: AGENT_API.qualite.actions,
      }),
    inbox: [
      { key: 'nonConformites', dataKey: 'nonConformites' },
      { key: 'audits', dataKey: 'audits' },
    ],
    panels: {
      nc: { dataKey: 'nonConformites', titleFields: ['reference', 'title'], subtitleFields: ['severity', 'status'] },
      audits: { dataKey: 'audits', titleFields: ['title', 'reference'], subtitleFields: ['date', 'status'] },
    },
  }),
  import_export: moduleDef('import_export', {
    fetch: () =>
      fetchParallel({
        importDecl: AGENT_API.import_export.importDecl,
        exportDecl: AGENT_API.import_export.exportDecl,
        shipping: AGENT_API.import_export.shipping,
        inspections: AGENT_API.import_export.inspections,
      }),
    inbox: [
      { key: 'importDecl', dataKey: 'importDecl' },
      { key: 'inspections', dataKey: 'inspections' },
    ],
    panels: {
      douane: { dataKey: 'importDecl', titleFields: ['reference', 'declaration_number'], subtitleFields: ['status', 'customs_office'] },
      expeditions: { dataKey: 'shipping', titleFields: ['reference', 'tracking_number'], subtitleFields: ['destination', 'status'] },
    },
  }),
  parc_auto: moduleDef('parc_auto', {
    fetch: () =>
      fetchParallel({
        vehicles: AGENT_API.parc_auto.vehicles,
        maintenance: AGENT_API.parc_auto.maintenance,
        insurance: AGENT_API.parc_auto.insurance,
        violations: AGENT_API.parc_auto.violations,
      }),
    inbox: [
      { key: 'maintenance', dataKey: 'maintenance' },
      { key: 'insurance', dataKey: 'insurance' },
    ],
    panels: {
      vehicules: { dataKey: 'vehicles', titleFields: ['registration', 'plate', 'name'], subtitleFields: ['model', 'status'] },
      carburant: { dataKey: 'violations', titleFields: ['vehicle', 'reference'], subtitleFields: ['type', 'date'], filterActionable: false },
    },
  }),
  logistique: moduleDef('logistique', {
    fetch: () =>
      fetchParallel({
        incidents: AGENT_API.logistique.incidents,
        deliveries: AGENT_API.logistique.deliveries,
        manifests: AGENT_API.logistique.manifests,
      }),
    inbox: [{ key: 'incidents', dataKey: 'incidents' }],
    panels: {
      colis: { dataKey: 'deliveries', titleFields: ['tracking', 'reference'], subtitleFields: ['destination', 'status'] },
      routes: { dataKey: 'manifests', titleFields: ['route', 'reference'], subtitleFields: ['driver', 'status'] },
    },
  }),
  distribution: moduleDef('distribution', {
    fetch: () =>
      fetchParallel({
        planning: AGENT_API.distribution.planning,
        routes: AGENT_API.distribution.routes,
        orderPrep: AGENT_API.distribution.orderPrep,
      }),
    inbox: [{ key: 'planning', dataKey: 'planning' }],
    panels: {
      tournees: { dataKey: 'routes', titleFields: ['route', 'name'], subtitleFields: ['driver', 'stops'] },
      planning: { dataKey: 'planning', titleFields: ['date', 'route'], subtitleFields: ['status', 'orders'] },
    },
  }),
  livraison: moduleDef('livraison', {
    fetch: () =>
      fetchParallel({
        ongoing: AGENT_API.livraison.ongoing,
        refused: AGENT_API.livraison.refused,
        proofs: AGENT_API.livraison.proofs,
      }),
    inbox: [
      { key: 'ongoing', dataKey: 'ongoing' },
      { key: 'refused', dataKey: 'refused' },
    ],
    panels: {
      encours: { dataKey: 'ongoing', titleFields: ['reference', 'order_number'], subtitleFields: ['client', 'driver'] },
      preuves: { dataKey: 'proofs', titleFields: ['reference', 'delivery_id'], subtitleFields: ['status', 'date'] },
    },
  }),
  point_de_vente: moduleDef('point_de_vente', {
    fetch: () =>
      fetchParallel({
        sales: AGENT_API.point_de_vente.sales,
        stock: AGENT_API.point_de_vente.stock,
        payments: AGENT_API.point_de_vente.payments,
      }),
    inbox: [
      { key: 'stock', dataKey: 'stock' },
      { key: 'payments', dataKey: 'payments' },
    ],
    panels: {
      ventes: { dataKey: 'sales', titleFields: ['ticket', 'reference'], subtitleFields: ['amount', 'cashier'] },
      stock: { dataKey: 'stock', titleFields: ['product_name', 'name'], subtitleFields: ['quantity', 'min_stock'] },
    },
  }),
  marketing: moduleDef('marketing', {
    fetch: () =>
      fetchParallel({
        campaigns: AGENT_API.marketing.campaigns,
        budget: AGENT_API.marketing.budget,
        events: AGENT_API.marketing.events,
      }),
    inbox: [{ key: 'campaigns', dataKey: 'campaigns' }],
    panels: {
      campagnes: { dataKey: 'campaigns', titleFields: ['name', 'title'], subtitleFields: ['channel', 'status'] },
      roi: { dataKey: 'budget', titleFields: ['campaign', 'name'], subtitleFields: ['spent', 'roi'], filterActionable: false },
    },
  }),
  crm: moduleDef('crm', {
    fetch: () =>
      fetchParallel({
        leads: AGENT_API.crm.leads,
        opportunities: AGENT_API.crm.opportunities,
        tickets: AGENT_API.crm.tickets,
      }),
    inbox: [
      { key: 'leads', dataKey: 'leads' },
      { key: 'opportunities', dataKey: 'opportunities' },
    ],
    panels: {
      leads: { dataKey: 'leads', titleFields: ['name', 'company'], subtitleFields: ['source', 'status'] },
      pipeline: { dataKey: 'opportunities', titleFields: ['title', 'name'], subtitleFields: ['stage', 'amount'] },
    },
  }),
  service_client: moduleDef('service_client', {
    fetch: () =>
      fetchParallel({
        tickets: AGENT_API.service_client.tickets,
        sla: AGENT_API.service_client.sla,
        reclamations: AGENT_API.service_client.reclamations,
      }),
    inbox: [{ key: 'tickets', dataKey: 'tickets' }],
    panels: {
      tickets: { dataKey: 'tickets', titleFields: ['subject', 'title'], subtitleFields: ['client', 'priority'] },
      sla: { dataKey: 'sla', titleFields: ['ticket', 'reference'], subtitleFields: ['deadline', 'status'] },
    },
  }),
  taches: moduleDef('taches', {
    fetch: () =>
      fetchParallel({
        tasks: AGENT_API.taches.tasks,
        reminders: AGENT_API.taches.reminders,
        milestones: AGENT_API.taches.milestones,
      }),
    inbox: [
      { key: 'tasks', dataKey: 'tasks' },
      { key: 'reminders', dataKey: 'reminders' },
    ],
    panels: {
      mistaches: { dataKey: 'tasks', titleFields: ['title', 'name'], subtitleFields: ['due_date', 'priority'] },
      kanban: { dataKey: 'tasks', titleFields: ['title', 'name'], subtitleFields: ['column', 'assignee'] },
    },
  }),
  approbations: moduleDef('approbations', {
    fetch: async () => {
      const [budgetWorkflow, contractApprovals, mes] = await Promise.all([
        safeGet(AGENT_API.budget.workflow),
        safeGet(AGENT_API.contrats.approvals),
        safeGet(AGENT_API.approbations.mes),
      ]);
      return {
        workflows: [...budgetWorkflow, ...contractApprovals, ...mes],
        statuts: await safeGet(AGENT_API.approbations.statuts),
        mes: mes.length ? mes : [...budgetWorkflow, ...contractApprovals],
      };
    },
    inbox: [{ key: 'mes', dataKey: 'mes' }],
    panels: {
      workflows: { dataKey: 'workflows', titleFields: ['title', 'reference'], subtitleFields: ['module', 'status'] },
      statuts: { dataKey: 'statuts', titleFields: ['workflow', 'title'], subtitleFields: ['step', 'status'] },
    },
  }),
  planification: moduleDef('planification', {
    fetch: () =>
      fetchParallel({
        rappels: AGENT_API.planification.rappels,
        rendezvous: AGENT_API.planification.rendezvous,
        charge: AGENT_API.planification.charge,
        calendrier: AGENT_API.planification.calendrier,
      }),
    inbox: [
      { key: 'rappels', dataKey: 'rappels' },
      { key: 'rendezvous', dataKey: 'rendezvous' },
    ],
    panels: {
      calendrier: { dataKey: 'calendrier', titleFields: ['title', 'subject'], subtitleFields: ['start', 'participants'], filterActionable: false },
      charge: { dataKey: 'charge', titleFields: ['resource', 'team'], subtitleFields: ['load', 'capacity'], filterActionable: false },
    },
  }),
};

export async function loadGenericModuleData(moduleId) {
  const config = MODULE_BUILDER_CONFIGS[moduleId];
  if (!config) return { data: {}, documents: [] };

  const [data, documents] = await Promise.all([config.fetch(), safeGetDocuments(moduleId)]);
  return { data, documents, config };
}

export function buildGenericInboxItems({ data, config }) {
  if (!config?.inbox) return [];

  const items = [];
  config.inbox.forEach((source) => {
    const records = data[source.dataKey] || [];
    items.push(...buildInboxItemsFromSource(records, source, config.label));
  });

  return items.slice(0, INBOX_LIMIT);
}

export function buildGenericSidePanels({ data, documents, config, moduleId }) {
  const panels = {};
  const counts = {};

  if (config?.panels) {
    Object.entries(config.panels).forEach(([tabId, panelConfig]) => {
      const records = data[panelConfig.dataKey] || [];
      const items = mapRecordsToPanel(records, panelConfig, config.label);
      panels[tabId] = {
        type: 'list',
        title: getWorkspaceTabsForModule(moduleId).find((t) => t.id === tabId)?.label || tabId,
        items,
        count: items.length,
        emptyMessage: gb('module_empty', { module: config.label }),
      };
      counts[`${tabId}Count`] = items.length;
    });
  }

  const docItems = buildDocumentsPanel(documents, config.label);
  panels.documents = {
    type: 'documents',
    title: gb('recent_documents_title'),
    items: docItems,
    count: docItems.length,
    emptyMessage: gb('no_documents_module'),
  };
  counts.documentsCount = docItems.length;

  return { panels, counts };
}

export function buildGenericModuleContext({ data, config }) {
  if (!config) return null;

  const summary = {};
  Object.entries(data || {}).forEach(([key, rows]) => {
    summary[key] = {
      total: Array.isArray(rows) ? rows.length : 0,
      sample: (Array.isArray(rows) ? rows : []).slice(0, 5).map((row) => ({
        id: row.id,
        title: pickField(row, DEFAULT_TITLE_FIELDS),
        status: pickField(row, DEFAULT_STATUS_FIELDS),
      })),
    };
  });

  return {
    module: config.label,
    workspaceData: summary,
  };
}

export async function buildGenericModuleWorkspace(moduleId) {
  const loaded = await loadGenericModuleData(moduleId);
  const inboxItems = buildGenericInboxItems(loaded);
  const { panels, counts } = buildGenericSidePanels({ ...loaded, moduleId });

  return {
    inboxItems,
    panels,
    counts: {
      inboxCount: inboxItems.length,
      ...counts,
    },
    context: buildGenericModuleContext(loaded),
  };
}
